import * as React from 'react';
import FolderTreeType from 'mini-mail-folder-store/lib/store/schema/FolderTreeType';
import getChevronIcon from '../utils/getChevronIcon';
import getChevronIconAriaLabel from '../utils/getChevronIconAriaLabel';
import getDefaultIconForFolder from '../utils/getDefaultIconForFolder';
import getFolderItemAriaLabel from '../utils/getFolderItemAriaLabel';
import getFolderNodeViewState from 'mini-mail-folder-store/lib/selectors/getFolderNodeViewState';
import getTreeNodeViewId from '../utils/getTreeNodeViewId';
import getValidFolders from '../utils/getValidFolders';
import mailStore from 'owa-mail-store/lib/store/Store';
import ReadUnreadOrTotalCountPill from './ReadUnreadOrTotalCountPill';
import setFolderTreeType from 'mini-mail-folder-store/lib/actions/setFolderTreeType';
import setShowFolderPane from 'mini-mail-folder-store/lib/actions/setShowFolderPane';
import shouldDisplayTotalCount from '../utils/shouldDisplayTotalCount';
import toggleFolderNodeViewState from 'mini-mail-folder-store/lib/actions/toggleFolderNodeViewState';
import toggleFolderTreeExpansion from 'mini-mail-folder-store/lib/actions/toggleFolderTreeExpansion';
import { getRTL } from '@uifabric/utilities';
import { getStore as FolderStore } from 'mini-mail-folder-store/lib/store/Store';
import { logUsage } from 'owa-analytics';
import { MiniIcons } from 'owa-mini-icons';
import { selectFolderIntentional } from 'mini-mail-folder-actions/lib/utils/selectFolderIntentional';
import { Tree, TreeNodeChevronProps, TreeNodeData } from 'mini-tree';

const styles = require('./FolderPaneTree.scss');

export interface FolderPaneTreeProps {
    folderIds: string[];
    rootDisplayName: string;
    folderTreeType: FolderTreeType;
    isRootExpanded: boolean;
    itemRefs: React.RefObject<any>[];
    startIndex: number;
    totalLength?: number;
}

/**
 * Wrapper component to render FolderPaneTree nodes
 * @param props FolderPaneTreeProps
 */
class FolderPaneTree extends React.PureComponent<FolderPaneTreeProps, {}> {
    private getChevronProps = (
        folderId: string,
        childFolderIds: string[],
        isExpanded: boolean,
        isFavoriteTree: boolean
    ): TreeNodeChevronProps => {
        if (!isFavoriteTree && childFolderIds.length) {
            return {
                iconName: getChevronIcon(isExpanded, getRTL()),
                onClick: (depth: number) => {
                    toggleFolderNodeViewState(folderId);
                    logUsage('FolderPane', [depth, getFolderNodeViewState(folderId).isExpanded]);
                },
                getChevronIconAriaLabel,
            };
        }

        return {
            iconName: getDefaultIconForFolder(folderId, isFavoriteTree),
        };
    };

    private onFolderSelected = (folderId: string) => {
        selectFolderIntentional(folderId);
        setFolderTreeType(this.props.folderTreeType);
        setShowFolderPane(false /*showFolderPane*/);
    };

    private onRootNodeClicked = () => {
        toggleFolderTreeExpansion(this.props.folderTreeType);
    };

    private getNodeData = (folderId: string): TreeNodeData => {
        const folder = mailStore.folders.folderTable.get(folderId);
        const folderViewState = getFolderNodeViewState(folderId);
        const isSelected =
            folderId === mailStore.folders.selectedFolderId &&
            this.props.folderTreeType === FolderStore().selectedFolderTree;
        const filteredChildFolderIds = getValidFolders(folder.childFolderIds);
        const isTotalCount = shouldDisplayTotalCount(folder);
        const count = isTotalCount ? folder.TotalCount : folder.UnreadCount;
        const isFavoriteTree = this.props.folderTreeType === FolderTreeType.Favorites;
        const treeNodeViewId = getTreeNodeViewId(this.props.rootDisplayName, folderId);

        return {
            displayName: folder.DisplayName,
            iconProps: this.getChevronProps(
                folderId,
                filteredChildFolderIds,
                folderViewState.isExpanded,
                isFavoriteTree
            ),
            isExpanded: folderViewState.isExpanded,
            isSelected: isSelected,
            onClick: this.onFolderSelected,
            childNodeIds: !isFavoriteTree ? filteredChildFolderIds : [],
            rightCharm: (
                <ReadUnreadOrTotalCountPill
                    folderId={folderId}
                    isSelected={isSelected}
                    isTotalCount={isTotalCount}
                    treeNodeViewId={treeNodeViewId}
                />
            ),
            ariaLabelForNode: getFolderItemAriaLabel(
                folder.DisplayName,
                count,
                false /**isMoveToFolderPane */,
                isSelected,
                isTotalCount
            ),
            treeNodeViewId: treeNodeViewId,
        };
    };

    render() {
        const {
            folderIds,
            rootDisplayName,
            isRootExpanded,
            startIndex,
            itemRefs,
            totalLength,
        } = this.props;
        return (
            <div className={styles.treeContainer}>
                <Tree
                    rootDisplayName={rootDisplayName}
                    isRootExpanded={isRootExpanded}
                    nodeIds={folderIds}
                    rootIconName={isRootExpanded ? MiniIcons.ChevronDown : MiniIcons.ChevronRight}
                    onRootNodeClicked={this.onRootNodeClicked}
                    getNodeData={this.getNodeData}
                    startIndex={startIndex}
                    itemRefs={itemRefs}
                    totalLength={totalLength}
                    folderTreeType={this.props.folderTreeType}
                />
            </div>
        );
    }
}

export default FolderPaneTree;
